import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Phone, Mail, MapPin } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { SITE, BRAND } from "@/lib/content";
import { fileUrl } from "@/lib/api";
import { PalmSilhouette } from "@/components/site/Bits";

const QUICK = [["Home", "/"], ["About", "/about"], ["Board", "/board"], ["Tryouts", "/tryouts"], ["Teams", "/teams"], ["Athletes", "/athletes"], ["Events", "/events"], ["Media", "/media"], ["Sponsors", "/sponsors"], ["Contact", "/contact"]];

const TikTok = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.6 2.6 0 0 1 0-5.2c.27 0 .53.04.77.12V9.66a5.73 5.73 0 0 0-.77-.05A5.7 5.7 0 1 0 15.6 15.3V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3a4.3 4.3 0 0 1-3.3-1.48z" /></svg>
);
const XIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M17.75 3h3.07l-6.7 7.66L22 21h-6.17l-4.83-6.32L5.47 21H2.4l7.17-8.2L2 3h6.33l4.37 5.77L17.75 3zm-1.08 16.2h1.7L7.4 4.73H5.58L16.67 19.2z" /></svg>
);

const Socials = () => {
  const { settings } = useSite();
  const items = [["facebook", Facebook, settings.facebook_url], ["instagram", Instagram, settings.instagram_url], ["youtube", Youtube, settings.youtube_url], ["tiktok", TikTok, settings.tiktok_url], ["x", XIcon, settings.x_url]];
  return (
    <div className="flex gap-2" data-testid="footer-socials">
      {items.map(([k, Icon, url]) => (
        <a key={k} href={url || "#"} target={url ? "_blank" : undefined} rel="noreferrer" aria-label={k}
          title={url ? k : `${k} — link coming soon`} onClick={(e) => !url && e.preventDefault()}
          className={`flex h-10 w-10 items-center justify-center border border-line transition-colors ${url ? "hover:bg-gold hover:text-navy hover:border-gold" : "opacity-40 cursor-not-allowed"}`}
          data-testid={`footer-social-${k}`}>
          <Icon className="h-4 w-4" />
        </a>
      ))}
    </div>
  );
};

const FooterLogo = () => {
  const { settings } = useSite();
  return <img src={settings.logo_url ? fileUrl(settings.logo_url) : BRAND.logo} alt="Tri County Allstars logo" className="h-28 w-auto mb-6" />;
};

export const Footer = () => (
  <footer className="relative overflow-hidden bg-navy border-t-4 border-gold" data-testid="site-footer">
    <PalmSilhouette className="absolute right-4 -bottom-8 h-72 text-pitch/40" />
    <div className="relative wrap py-16 grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr]">
      <div>
        <FooterLogo />
        <p className="h-display text-5xl sm:text-6xl">Tri County<br /><span className="text-gold">Allstars</span></p>
        <p className="mt-4 font-display text-xl font-bold uppercase italic text-slate-200">{SITE.slogan}</p>
        <p className="mt-2 text-sm text-slate-400">{SITE.location}</p>
        <div className="mt-6"><Socials /></div>
      </div>
      <div className="space-y-4 text-sm">
        <p className="eyebrow">Contact</p>
        <a href={SITE.tel} className="flex items-center gap-3 hover:text-gold" data-testid="footer-phone"><Phone className="h-4 w-4 text-gold" /> Phone: {SITE.phone}</a>
        <a href={SITE.mailto} className="flex items-center gap-3 hover:text-gold break-all" data-testid="footer-email"><Mail className="h-4 w-4 text-gold shrink-0" /> Email: {SITE.email}</a>
        <p className="flex items-start gap-3 text-slate-300"><MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" /> Home Field: {SITE.field}, {SITE.address}</p>
      </div>
      <div>
        <p className="eyebrow mb-4">Quick Links</p>
        <ul className="grid grid-cols-2 gap-y-2 text-sm">
          {QUICK.map(([l, to]) => (
            <li key={to}><Link to={to} className="text-slate-300 hover:text-gold transition-colors" data-testid={`footer-link-${l.toLowerCase()}`}>{l}</Link></li>
          ))}
        </ul>
      </div>
    </div>
    <div className="relative border-t border-white/10">
      <div className="wrap flex flex-col sm:flex-row gap-3 justify-between py-6 text-xs text-slate-400">
        <p data-testid="footer-copyright">© {new Date().getFullYear()} Tri County Allstars. All rights reserved.</p>
        <div className="flex gap-5">
          <Link to="/privacy" className="hover:text-gold" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/login" className="hover:text-gold" data-testid="footer-staff-login">Staff & Parent Login</Link>
        </div>
      </div>
    </div>
  </footer>
);
